import type { Currency } from "@opensea/app-kit"
import { useBlockExplorer } from "@opensea/app-kit"
import {
  CenterAligned,
  classNames,
  Flex,
  FlexColumn,
  formatAddress,
  NumberDisplay,
  Spinner,
  TextBody,
  TextHeading,
  TextLabel,
  transitions,
} from "@opensea/ui-kit"
import { ArrowDownward, Check, Close, Send } from "@opensea/ui-kit/icons"
import { bn, type Nullable } from "@opensea/utils"
import { AnimatePresence, motion } from "motion/react"
import { useCallback, useState } from "react"
import { useTranslations } from "use-intl"
import { Avatar } from "../../../../components/Avatar"
import { Button } from "../../../../components/Button"
import type {
  SchedulableResponse,
  SchedulableTransactionResponse,
} from "../../../../polychain/hooks/useScheduler"
import { useScheduler } from "../../../../polychain/hooks/useScheduler"
import { FlowActions } from "../../../../stores/flowStore"
import type { TransferTokensActionsResponse } from "../hooks/useTransferTokens"

const iconClassNames = classNames("transition duration-1000 ease-out-quint")

const headingClassNames = classNames(
  "fixed w-full text-center font-normal",
  "transition duration-500 ease-out-quint",
)

type SendingProps = {
  actions: TransferTokensActionsResponse
  currency: Currency
  quantity: string
  ensName: string | undefined
  imageUrl: Nullable<string>
  address: string
  onBack: () => void
}

export function Sending({
  actions,
  currency,
  quantity,
  ensName,
  imageUrl,
  address,
  onBack,
}: SendingProps) {
  const t = useTranslations("wallet.SendFlow")
  const [isDone, setIsDone] = useState(false)
  const [transactionHash, setTransactionHash] = useState<string>()
  const { getTransactionUrl, getName } = useBlockExplorer(
    currency.chainIdentifier,
  )

  const { isLoading, isPending, error, retry } = useScheduler({
    actions,
    onAllActionsCompleted: async (lastResponse: SchedulableResponse) => {
      const tx = lastResponse as SchedulableTransactionResponse

      setTransactionHash(tx.transactionIdentifier.hash)

      const receipt = await tx.wait({
        confirmations: 1,
        timeout: 60 * 2 * 1000,
      })

      if (receipt.status === "success") {
        FlowActions.onSuccess()
      }

      setIsDone(true)
    },
    autoStartEmbeddedWallet: true,
  })

  const explorerUrl = transactionHash
    ? getTransactionUrl(transactionHash)
    : undefined

  const handleClose = useCallback(() => {
    if (transactionHash) {
      FlowActions.endFlow()
    } else {
      onBack()
    }
  }, [transactionHash, onBack])

  const isSending = !error && (!isDone || isLoading || isPending)

  return (
    <AnimatePresence>
      <FlexColumn className="h-full items-center justify-between">
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="w-full"
          exit={{ opacity: 0, scale: 0.95 }}
          initial={{ opacity: 0, scale: 0.95 }}
        >
          <FlexColumn className="items-center gap-5 py-16">
            <SendingIcon error={error} isSending={isSending} />

            <div className="relative h-10 w-full">
              <TextHeading
                className={classNames(
                  headingClassNames,
                  isSending ? "opacity-100" : "translate-x-[25px] opacity-0",
                )}
                size="lg"
              >
                {t("sending.sendingStatus")}
              </TextHeading>
              <TextHeading
                className={classNames(
                  headingClassNames,
                  isSending && "translate-x-[-25px] opacity-0",
                )}
                size="lg"
              >
                {error ? t("sending.errorStatus") : t("sending.sentStatus")}
              </TextHeading>
            </div>

            <FlexColumn className="items-center gap-3">
              <Flex className="items-center gap-3">
                <NumberDisplay
                  className="font-medium text-display-lg"
                  value={quantity}
                />
                <TextBody className="font-mono text-display-lg text-secondary">
                  {currency.symbol}
                </TextBody>
              </Flex>

              <NumberDisplay
                className="text-text-secondary"
                display="usd"
                value={bn(quantity).times(currency.usdPrice).toString()}
              />
            </FlexColumn>
          </FlexColumn>
        </motion.div>

        <motion.div
          animate={{ translateY: "0%" }}
          className="w-full"
          exit={{ translateY: "100%" }}
          initial={{ translateY: "100%" }}
          transition={{ ...transitions.default, duration: 1 }}
        >
          <FlexColumn className="relative w-full grow gap-4 rounded-t-2xl border-border-2 border-x border-t bg-bg-additional-1">
            <Flex className="-top-5 absolute w-full justify-center">
              <div className="rounded-full bg-bg-additional-3 p-2">
                <ArrowDownward size={24} />
              </div>
            </Flex>

            <FlexColumn className="items-center gap-6 px-9 pt-9 text-center">
              <TextLabel className="text-text-secondary" size="sm">
                {t("sending.recipientWalletLabel")}
              </TextLabel>
              <FlexColumn className="items-center gap-2">
                {imageUrl && (
                  <Avatar className="rounded-full" size={64} src={imageUrl} />
                )}
                {ensName ? (
                  <FlexColumn className="gap-1">
                    <TextHeading className="font-medium font-mono" size="md">
                      {ensName}
                    </TextHeading>
                    <TextBody className="font-mono text-secondary">
                      {formatAddress(address)}
                    </TextBody>
                  </FlexColumn>
                ) : (
                  <TextHeading className="font-mono font-normal" size="md">
                    {formatAddress(address)}
                  </TextHeading>
                )}
              </FlexColumn>
            </FlexColumn>

            <FlexColumn className="grow items-center justify-end gap-4 px-6 pb-6">
              <FlexColumn className="w-full gap-4">
                {error ? (
                  <Button onClick={retry} size="lg" variant="destructive">
                    {t("sending.retryButton")}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    disabled={!explorerUrl}
                    onClick={() => {
                      window.open(explorerUrl, "_blank")
                    }}
                    size="lg"
                    variant="tertiary"
                  >
                    {isLoading
                      ? t("sending.confirmInWalletButton")
                      : t("sending.viewOnExplorerButton", {
                          explorerName: getName(),
                        })}
                  </Button>
                )}

                <Button
                  className="w-full"
                  onClick={handleClose}
                  size="lg"
                  variant="secondary"
                >
                  {t("sending.closeButton")}
                </Button>
              </FlexColumn>
              <TextBody className="font-mono text-secondary" size="xs">
                {t("sending.followWalletPrompt")}
              </TextBody>
            </FlexColumn>
          </FlexColumn>
        </motion.div>
      </FlexColumn>
    </AnimatePresence>
  )
}

const SendingIcon = ({
  isSending,
  error,
}: {
  isSending: boolean
  error: string | undefined
}) => {
  return (
    <CenterAligned
      className={classNames(
        "size-[100px] overflow-hidden rounded-full",
        error
          ? "bg-error-1-transparent-1"
          : isSending
            ? "bg-bg-additional-1"
            : "bg-success-1-transparent-1",
        iconClassNames,
        "transition-colors",
      )}
    >
      <Send
        className={classNames(
          isSending && !error ? "translate-x-0" : "translate-x-[100px]",
          iconClassNames,
        )}
        size={44}
      />
      {/* h = 30 * (40 / inner path) */}
      {error ? (
        <Close
          className={classNames(
            "fixed scale-100 text-error-1 opacity-100",
            iconClassNames,
          )}
          size={59.88}
        />
      ) : (
        <Check
          className={classNames(
            "fixed text-success-1",
            isSending ? "scale-0 opacity-0" : "scale-100 opacity-100",
            iconClassNames,
          )}
          size={59.88}
        />
      )}

      <Spinner
        className={classNames(
          "fixed size-[100px] opacity-25",
          !isSending && "opacity-0",
        )}
      />
    </CenterAligned>
  )
}
